import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { setToken, getToken, fetchMe, logout as apiLogout, verifyMagic, confirmDevice as apiConfirmDevice } from "@/lib/api";
import { getDeviceFingerprint } from "@/lib/fingerprint";
import { useI18n } from "@/i18n/I18nContext";
import { applyTextScale } from "@/lib/textScale";

const UserContext = createContext(null);

// Iter 25: White-label theming. Convert a hex color to a darker shade (hover)
// and inject both as CSS custom properties so existing Tailwind text-[#...]
// classes keep working alongside dynamic vars.
const _hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};
const _shade = (hex, factor) => {
  const { r, g, b } = _hexToRgb(hex) || { r: 22, g: 163, b: 74 };
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const f = (v) => clamp(v * factor).toString(16).padStart(2, "0");
  return `#${f(r)}${f(g)}${f(b)}`;
};

// Contraste WCAG entre un color y el blanco.
const _contrastOnWhite = (hex) => {
  const { r, g, b } = _hexToRgb(hex) || { r: 0, g: 0, b: 0 };
  const lin = (v) => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return 1.05 / (L + 0.05);
};
// Texto oscuro de marca (títulos, cifras): el verde oscuro de Colygon o, con
// color de empresa, su sombra más clara que se lea sobre blanco y fondos
// suaves (≥ 7:1). Antes era el mismo tono que el hover de los botones y en
// fondos verdes claros no llegaba al contraste mínimo.
const _textShade = (hex) => {
  for (let f = 0.6; f > 0.05; f -= 0.05) {
    const c = _shade(hex, f);
    if (_contrastOnWhite(c) >= 7) return c;
  }
  return "#0f172a";
};

const applyBrandTheme = (primaryColor) => {
  // Default to Colygon green when no company override.
  const primary = primaryColor || "#16A34A";
  const dark = _shade(primary, 0.75);
  const light = _shade(primary, 1.6);
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand-primary-dark", dark);
  root.style.setProperty("--brand-primary-light", light);
  root.style.setProperty("--brand-primary-text", primaryColor ? _textShade(primary) : "#14532D");
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const { lang, setLang } = useI18n();

  // Apply theme on every user load/refresh.
  useEffect(() => {
    applyBrandTheme(user?.company_primary_color);
  }, [user?.company_primary_color]);

  // Sincronizar el idioma al cargar el usuario (una vez por login):
  //  - Si tiene idioma guardado → adoptarlo (sigue al usuario entre dispositivos).
  //  - Si no lo tiene → persistir el idioma actual de la UI (backfill de usuarios
  //    antiguos, para que sus emails dejen de salir en el default).
  // setLang ya persiste al backend cuando hay sesión.
  useEffect(() => {
    if (!user?.id) return;
    const pl = user.profile_lang;
    if (pl && pl !== lang) setLang(pl);
    else if (!pl) setLang(lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Tamaño del texto guardado en el perfil: sigue a la persona entre dispositivos
  useEffect(() => {
    if (user?.text_scale) applyTextScale(user.text_scale);
  }, [user?.text_scale]);

  useEffect(() => {
    if (!getToken()) {
      setInitialized(true);
      applyBrandTheme(null);
      return;
    }
    fetchMe()
      .then(setUser)
      .catch(() => {
        setToken(null);
        localStorage.removeItem("colygon_tenant_override");
        localStorage.removeItem("colygon_offline_me");
      })
      .finally(() => setInitialized(true));
  }, []);

  const refresh = useCallback(async () => {
    try { const u = await fetchMe(); setUser(u); return u; }
    catch { setToken(null); setUser(null); return null; }
  }, []);

  const loginWithMagic = useCallback(async (token, name, companyCode, dniNif) => {
    let fingerprint = null;
    try { fingerprint = await getDeviceFingerprint(); } catch { /* ignore */ }
    const res = await verifyMagic(token, name, fingerprint, companyCode, dniNif);
    if (res?.session_token) {
      setToken(res.session_token);
      setUser(res.user);
    }
    return res;
  }, []);

  const confirmDevice = useCallback(async (token) => {
    const res = await apiConfirmDevice(token);
    if (res?.session_token) {
      setToken(res.session_token);
      setUser(res.user);
    }
    return res;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setToken(null);
    setUser(null);
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, initialized, refresh, loginWithMagic, confirmDevice, logout }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within UserProvider");
  return ctx;
};
